/**
 * /admin/invitations — 用户邀请管理(teacher-role spec §5.1/§5.9)。
 * 双通道:邀请链接(SMTP 缺失,复制链接送达)+ 直建(园长代设初始密码)。
 * CSV 批量;列表含状态筛选/重发(轮换 token)/撤销。
 */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useBulkInvite,
  useCreateInvitation,
  useCreateSubject,
  useInvitations,
  useResendInvitation,
  useRevokeInvitation,
  useOrgMe,
  useSubjects,
  type Invitation,
} from "@/api/school";

const STATUS_CLS: Record<string, string> = {
  pending: "bg-sky-100 text-sky-700",
  accepted: "bg-emerald-100 text-emerald-700",
  expired: "bg-amber-100 text-amber-700",
  revoked: "bg-slate-200 text-slate-500",
};

export default function InvitationsAdminPage() {
  const { t } = useTranslation();
  const [statusF, setStatusF] = useState("");
  const list = useInvitations(statusF || undefined);
  const resend = useResendInvitation();
  const revoke = useRevokeInvitation();
  const bulk = useBulkInvite();
  const [showInvite, setShowInvite] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [bulkResult, setBulkResult] = useState<Awaited<ReturnType<typeof bulk.mutateAsync>> | null>(null);

  function copy(url: string, id: string) {
    void navigator.clipboard.writeText(new URL(url, window.location.origin).toString());
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <header className="mb-5 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">{t("invitations.title")}</h1>
          <p className="text-xs text-slate-500 mt-1">{t("invitations.hint")}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            className="px-3 py-1.5 text-sm rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
          >
            {t("invitations.csv")}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (f) setBulkResult(await bulk.mutateAsync(f));
              e.target.value = "";
            }}
          />
          <button
            onClick={() => setShowInvite(true)}
            className="px-3 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600"
          >
            {t("invitations.new")}
          </button>
        </div>
      </header>

      {bulkResult && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4 text-sm">
          <p className="mb-2 text-slate-700">
            {t("invitations.bulkResult", { ok: bulkResult.succeeded, fail: bulkResult.failed })}
          </p>
          <ul className="max-h-40 overflow-y-auto text-xs space-y-1">
            {bulkResult.results.map((r) => (
              <li key={r.line} className={r.ok ? "text-emerald-600" : "text-rose-600"}>
                L{r.line} {r.email}:{r.ok ? "✓" : r.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-3">
        <select
          value={statusF}
          onChange={(e) => setStatusF(e.target.value)}
          className="rounded border border-slate-200 px-2 py-1.5 text-xs"
        >
          <option value="">{t("invitations.allStatuses")}</option>
          {["pending", "accepted", "expired", "revoked"].map((s2) => (
            <option key={s2} value={s2}>{t(`invitations.status.${s2}`)}</option>
          ))}
        </select>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-2 font-medium">{t("invitations.cols.email")}</th>
              <th className="text-left px-4 py-2 font-medium">{t("invitations.cols.name")}</th>
              <th className="text-left px-4 py-2 font-medium">{t("invitations.cols.role")}</th>
              <th className="text-left px-4 py-2 font-medium">{t("invitations.cols.status")}</th>
              <th className="text-left px-4 py-2 font-medium">{t("invitations.cols.expires")}</th>
              <th className="px-4 py-2 w-44"></th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((inv: Invitation) => (
              <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5 text-slate-800">{inv.email}</td>
                <td className="px-4 py-2.5 text-slate-600">{inv.name ?? "—"}</td>
                <td className="px-4 py-2.5 text-slate-600">{t(`invitations.roles.${inv.role}`, inv.role)}</td>
                <td className="px-4 py-2.5">
                  <span className={`px-2 py-0.5 rounded text-xs ${STATUS_CLS[inv.status]}`}>
                    {t(`invitations.status.${inv.status}`)}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-400">{inv.expires_at.slice(0, 10)}</td>
                <td className="px-4 py-2.5 text-right space-x-1">
                  {inv.status === "pending" && inv.invite_url && (
                    <button
                      onClick={() => copy(inv.invite_url!, inv.id)}
                      className="px-2 py-1 text-xs rounded border border-sky-200 text-sky-600 hover:bg-sky-50"
                    >
                      {copied === inv.id ? "✓" : t("invitations.copyLink")}
                    </button>
                  )}
                  {(inv.status === "pending" || inv.status === "expired") && (
                    <button
                      onClick={() => resend.mutate(inv.id)}
                      className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                    >
                      {t("invitations.resend")}
                    </button>
                  )}
                  {inv.status === "pending" && (
                    <button
                      onClick={() => revoke.mutate(inv.id)}
                      className="px-2 py-1 text-xs rounded border border-rose-200 text-rose-500 hover:bg-rose-50"
                    >
                      {t("invitations.revoke")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showInvite && <InviteDialog onClose={() => setShowInvite(false)} />}
    </div>
  );
}

function InviteDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const subjects = useSubjects();
  const create = useCreateInvitation();
  const createSubject = useCreateSubject();
  // 园长 / 管理员账号只能由集团管理员邀请(后端同样拦截)
  const isGroupAdmin = !!useOrgMe().data?.is_admin;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("teacher");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [addingSubject, setAddingSubject] = useState(false);
  const [newSubjName, setNewSubjName] = useState("");
  const [newSubjSlug, setNewSubjSlug] = useState("");
  const [direct, setDirect] = useState(false);
  const [initialPw, setInitialPw] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState<boolean | null>(null);
  const [provisioned, setProvisioned] = useState(false);

  const activeSubjects = (subjects.data ?? []).filter(
    (s) => (s as { status?: string }).status !== "archived",
  );

  async function addSubject() {
    setErr(null);
    const slug = newSubjSlug.trim().toLowerCase();
    const name = newSubjName.trim();
    if (!name) return;
    if (!/^[a-z0-9][a-z0-9-]*$/.test(slug)) {
      setErr(t("invitations.subjectSlugInvalid"));
      return;
    }
    try {
      const s = await createSubject.mutateAsync({ slug, name });
      setPicked(new Set([...picked, s.id]));
      setAddingSubject(false);
      setNewSubjName("");
      setNewSubjSlug("");
    } catch (e) {
      const d = (e as { detail?: { message?: string; error?: string } }).detail;
      setErr(d?.message ?? d?.error ?? (e as Error).message);
    }
  }

  async function submit() {
    setErr(null);
    try {
      const inv = await create.mutateAsync({
        email, name: name || undefined, role,
        subject_ids: Array.from(picked),
        initial_password: direct ? initialPw : undefined,
      });
      if (direct) setProvisioned(true);
      else {
        setResultUrl(inv.invite_url ?? null);
        setEmailSent((inv as { email_sent?: boolean }).email_sent ?? null);
      }
    } catch (e) {
      const d = (e as { detail?: { message?: string; error?: string } }).detail;
      setErr(d?.message ?? d?.error ?? (e as Error).message);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={onClose}>
      <div className="w-[28rem] rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-base font-semibold text-slate-800 mb-3">{t("invitations.new")}</h2>

        {resultUrl || provisioned ? (
          <div>
            {provisioned ? (
              <p className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                {t("invitations.directDone")}
              </p>
            ) : (
              <>
                <p className="mb-2 text-sm text-slate-600">
                  {emailSent ? t("invitations.emailSent") : t("invitations.linkReady")}
                </p>
                <div className="mb-4 flex gap-2">
                  <input
                    readOnly
                    value={new URL(resultUrl!, window.location.origin).toString()}
                    className="flex-1 rounded border border-slate-200 px-2 py-1.5 text-xs text-slate-500"
                  />
                  <button
                    onClick={() =>
                      void navigator.clipboard.writeText(new URL(resultUrl!, window.location.origin).toString())
                    }
                    className="px-3 py-1.5 rounded bg-sky-600 text-white text-xs"
                  >
                    {t("invitations.copyLink")}
                  </button>
                </div>
              </>
            )}
            <div className="flex justify-end">
              <button onClick={onClose} className="px-3 py-1.5 rounded bg-slate-600 text-white text-sm">
                {t("transfer.done")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <label className="block text-sm mb-2">
              <span className="text-slate-600">{t("invitations.cols.email")} *</span>
              <input value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
            </label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <label className="block text-sm">
                <span className="text-slate-600">{t("invitations.cols.name")}</span>
                <input value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" />
              </label>
              <label className="block text-sm">
                <span className="text-slate-600">{t("invitations.cols.role")}</span>
                <select value={role} onChange={(e) => setRole(e.target.value)}
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm">
                  <option value="teacher">{t("invitations.roles.teacher")}</option>
                  {isGroupAdmin && <option value="principal">{t("invitations.roles.principal")}</option>}
                </select>
              </label>
            </div>
            <div className="mb-3">
              <span className="text-sm text-slate-600">{t("invitations.subjects")} *</span>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {activeSubjects.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      const next = new Set(picked);
                      if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                      setPicked(next);
                    }}
                    className={`px-2 py-1 rounded-full text-xs border ${
                      picked.has(s.id)
                        ? "bg-sky-600 text-white border-sky-600"
                        : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    {s.name}
                  </button>
                ))}
                {/* 园长可即时新建学科并自动选中(Jarod 反馈)。 */}
                <button
                  type="button"
                  onClick={() => setAddingSubject((v) => !v)}
                  className="px-2 py-1 rounded-full text-xs border border-dashed border-slate-300 text-slate-500 hover:bg-slate-50"
                >
                  + {t("invitations.addSubject")}
                </button>
              </div>
              {addingSubject && (
                <div className="mt-2 flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
                  <label className="block text-xs">
                    <span className="text-slate-500">{t("invitations.subjectName")}</span>
                    <input
                      value={newSubjName}
                      onChange={(e) => setNewSubjName(e.target.value)}
                      placeholder={String(t("invitations.subjectNamePlaceholder"))}
                      className="mt-0.5 w-28 rounded border border-slate-300 px-2 py-1 text-sm"
                    />
                  </label>
                  <label className="block text-xs">
                    <span className="text-slate-500">{t("invitations.subjectSlug")}</span>
                    <input
                      value={newSubjSlug}
                      onChange={(e) => setNewSubjSlug(e.target.value)}
                      placeholder="e.g. art"
                      className="mt-0.5 w-28 rounded border border-slate-300 px-2 py-1 text-sm font-mono"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={addSubject}
                    disabled={!newSubjName.trim() || !newSubjSlug.trim() || createSubject.isPending}
                    className="rounded bg-sky-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
                  >
                    {t("invitations.addSubjectConfirm")}
                  </button>
                </div>
              )}
            </div>
            <label className="mb-2 flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={direct} onChange={(e) => setDirect(e.target.checked)} />
              {t("invitations.directMode")}
            </label>
            {direct && (
              <label className="block text-sm mb-3">
                <span className="text-slate-600">{t("invitations.initialPw")}(≥12 位)</span>
                <input value={initialPw} onChange={(e) => setInitialPw(e.target.value)}
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm font-mono" />
              </label>
            )}
            {err && <p className="mb-2 text-xs text-rose-600">{err}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-1.5 rounded border border-slate-200 text-sm text-slate-600">
                {t("common.cancel")}
              </button>
              <button
                onClick={submit}
                disabled={!email || (role !== "admin" && picked.size === 0) ||
                  (direct && initialPw.length < 12) || create.isPending}
                className="px-3 py-1.5 rounded bg-sky-600 text-white text-sm disabled:opacity-50"
              >
                {direct ? t("invitations.createNow") : t("invitations.send")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
